export type Point2D = { x: number; y: number };

const getSignedPolygonArea = (pointList: Point2D[]): number => {
  let area = 0;
  const n = pointList.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += pointList[i].x * pointList[j].y;
    area -= pointList[j].x * pointList[i].y;
  }

  return area / 2;
};

const ensureCCW = (pointList: Point2D[]) =>
  getSignedPolygonArea(pointList) < 0 ? [...pointList].reverse() : pointList;

export const getPolygonArea = (pointList: Point2D[]): number => {
  let area = 0;
  const n = pointList.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += pointList[i].x * pointList[j].y;
    area -= pointList[j].x * pointList[i].y;
  }

  return Math.abs(area) / 2;
};

const getLineIntersection = (
  p1: Point2D,
  p2: Point2D,
  p3: Point2D,
  p4: Point2D,
) => {
  const [d1x, d1y, d2x, d2y] = [
    p2.x - p1.x,
    p2.y - p1.y,
    p4.x - p3.x,
    p4.y - p3.y,
  ];
  const cross = d1x * d2y - d1y * d2x;

  if (Math.abs(cross) < 1e-10) return null;

  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / cross;

  return {
    x: p1.x + t * d1x,
    y: p1.y + t * d1y,
  } as Point2D;
};

const isInside = (point: Point2D, edgeStart: Point2D, edgeEnd: Point2D) =>
  (edgeEnd.x - edgeStart.x) * (point.y - edgeStart.y) -
    (edgeEnd.y - edgeStart.y) * (point.x - edgeStart.x) >=
  0;

export const clipPolygon = (
  subjectPolygon: Point2D[],
  clipPolygonPointList: Point2D[],
) => {
  if (subjectPolygon.length < 3 || clipPolygonPointList.length < 3) {
    return [];
  }

  const subject = ensureCCW(subjectPolygon);
  const clip = ensureCCW(clipPolygonPointList);

  let outputList = [...subject];

  for (let i = 0; i < clipPolygonPointList.length; i++) {
    if (outputList.length === 0) return [];

    const inputList = [...outputList];
    outputList = [];

    const edgeStart = clip[i];
    const edgeEnd = clip[(i + 1) % clip.length];

    for (let j = 0; j < inputList.length; j++) {
      const current = inputList[j];
      const previous = inputList[(j + inputList.length - 1) % inputList.length];

      const currentInside = isInside(current, edgeStart, edgeEnd);
      const previousInside = isInside(previous, edgeStart, edgeEnd);

      if (currentInside) {
        if (!previousInside) {
          const intersection = getLineIntersection(
            previous,
            current,
            edgeStart,
            edgeEnd,
          );

          intersection && outputList.push(intersection);
        }
        outputList.push(current);
      } else if (previousInside) {
        const intersection = getLineIntersection(
          previous,
          current,
          edgeStart,
          edgeEnd,
        );
        if (intersection) outputList.push(intersection);
      }
    }
  }

  return outputList;
};

export const getOverlapRatio = (polygon1: Point2D[], polygon2: Point2D[]) => {
  const clipped = clipPolygon(polygon1, polygon2);
  if (clipped.length < 3) return 0;

  const intersectionArea = getPolygonArea(clipped);
  const polygon1Area = getPolygonArea(polygon1);

  if (polygon1Area === 0) return 0;

  return intersectionArea / polygon1Area;
};
